import type { TFunction } from "@/i18n/catalog";

/** The options every config surface offers for timezone, currency, weight unit and country.
 *
 * One list per field, used by the organisation forms and by `ConfigOverrides` (the
 * account/site/kitchen override picker). They used to be private constants inside
 * `ConfigOverrides`, which is why the two organisation forms could not reach them and
 * shipped free-text inputs instead — that is how `Europe/Derlin` reached the database
 * and broke every waste aggregate for the org (POS2-1140).
 *
 * **To support a new region, add a line here — nothing else.** The API validates that a
 * timezone is a real IANA name and that a currency is a well-formed code, deliberately
 * *not* that either is in these lists, so widening the product's reach never needs a
 * backend change or a deploy. The lists are the menu; the API is the contract.
 *
 * Keep them sorted by region so a reader can see the estate at a glance. Current estate:
 * Ireland, Britain, Germany.
 */

export interface ConfigOption {
  value: string;
  label: string;
}

/** A list whose wording is copy rather than an identifier, so it carries a key and is
 * resolved at render through `configOptions` (POS2-1134). Literal keys in a lookup table —
 * `check-i18n.mjs` resolves keys statically, so a key built at runtime is invisible to it. */
export interface ConfigOptionKey {
  value: string;
  labelKey: string;
}

/** `UTC` is included on purpose: it is a legitimate choice and the runtime fallback in
 * `waste_service_time.py` and `open_meteo.py`, so an org set to it must stay selectable. */
export const TIMEZONE_OPTIONS: readonly ConfigOption[] = [
  { value: "Europe/London", label: "Europe/London (GMT)" },
  { value: "Europe/Dublin", label: "Europe/Dublin (GMT)" },
  { value: "Europe/Berlin", label: "Europe/Berlin (CET)" },
  { value: "Europe/Paris", label: "Europe/Paris (CET)" },
  { value: "Europe/Stockholm", label: "Europe/Stockholm (CET)" },
  { value: "Europe/Oslo", label: "Europe/Oslo (CET)" },
  { value: "Europe/Zurich", label: "Europe/Zurich (CET)" },
  { value: "America/New_York", label: "America/New_York (EST)" },
  { value: "America/Chicago", label: "America/Chicago (CST)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (PST)" },
  { value: "America/Toronto", label: "America/Toronto (EST)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (AEST)" },
  { value: "Australia/Melbourne", label: "Australia/Melbourne (AEST)" },
  { value: "UTC", label: "UTC" },
] as const;

export const CURRENCY_OPTIONS: readonly ConfigOptionKey[] = [
  { value: "GBP", labelKey: "settings.currencyOptionGBP" },
  { value: "EUR", labelKey: "settings.currencyOptionEUR" },
  { value: "USD", labelKey: "settings.currencyOptionUSD" },
  { value: "SEK", labelKey: "settings.currencyOptionSEK" },
  { value: "NOK", labelKey: "settings.currencyOptionNOK" },
  { value: "CHF", labelKey: "settings.currencyOptionCHF" },
  { value: "AUD", labelKey: "settings.currencyOptionAUD" },
  { value: "CAD", labelKey: "settings.currencyOptionCAD" },
] as const;

/** The countries the catalogue country tier can be keyed on (POS2-1144).
 *
 * Both sides of that tier read this one list — `organisations.country_code` through the two
 * organisation forms, `inventory_lists.country_code` through the catalogue admin — because the
 * two are compared exactly and two free-text fields drifted into `de` against `DE`, which
 * resolves to no country tier at all and silently classifies against the master catalogue.
 *
 * Same reach as the two lists above, and a shape rule rather than a membership one at the API
 * (`backend/app/core/countries.py` accepts any two letters, upper-cased), so adding a country
 * here needs no backend deploy either.
 */
export const COUNTRY_OPTIONS: readonly ConfigOptionKey[] = [
  { value: "IE", labelKey: "settings.countryOptionIE" },
  { value: "GB", labelKey: "settings.countryOptionGB" },
  { value: "DE", labelKey: "settings.countryOptionDE" },
  { value: "FR", labelKey: "settings.countryOptionFR" },
  { value: "SE", labelKey: "settings.countryOptionSE" },
  { value: "NO", labelKey: "settings.countryOptionNO" },
  { value: "CH", labelKey: "settings.countryOptionCH" },
  { value: "US", labelKey: "settings.countryOptionUS" },
  { value: "CA", labelKey: "settings.countryOptionCA" },
  { value: "AU", labelKey: "settings.countryOptionAU" },
] as const;

/** Closed set, unlike the two above: code that converts weights has to know every unit,
 * so adding one is a code change rather than a list edit. */
export const WEIGHT_UNIT_OPTIONS: readonly ConfigOptionKey[] = [
  { value: "kg", labelKey: "settings.weightUnitOptionKG" },
  { value: "g", labelKey: "settings.weightUnitOptionG" },
  { value: "lb", labelKey: "settings.weightUnitOptionLB" },
  { value: "oz", labelKey: "settings.weightUnitOptionOZ" },
] as const;

/** How a site is classified. Was a `TYPE_LABELS` map private to `pages/settings/sites.tsx`, which
 * is why its own edit dialog could only reach it through a `typeLabels` prop (POS2-1176). */
export const SITE_TYPE_OPTIONS = [
  { value: "hospital", labelKey: "settings.siteTypeHospital" },
  { value: "corporate", labelKey: "settings.siteTypeCorporate" },
  { value: "school", labelKey: "settings.siteTypeSchool" },
  { value: "events", labelKey: "settings.siteTypeEvents" },
  { value: "other", labelKey: "settings.siteTypeOther" },
] as const satisfies readonly ConfigOptionKey[];

/** How a kitchen is classified. Separate keys from `SITE_TYPE_OPTIONS` even where the English
 * agrees (`Events`, `Other`): the two are different enums, and one may be reworded without the
 * other. `check-i18n.mjs` check 5 holds their German identical for as long as the English is. */
export const KITCHEN_TYPE_OPTIONS = [
  { value: "main_kitchen", labelKey: "settings.kitchenTypeMainKitchen" },
  { value: "cafe", labelKey: "settings.kitchenTypeCafe" },
  { value: "kiosk", labelKey: "settings.kitchenTypeKiosk" },
  { value: "ward", labelKey: "settings.kitchenTypeWard" },
  { value: "events", labelKey: "settings.kitchenTypeEvents" },
  { value: "other", labelKey: "settings.kitchenTypeOther" },
] as const satisfies readonly ConfigOptionKey[];

/** Where in a kitchen a device sits.
 *
 * **This list is the enum**, and `pages/settings/devices.tsx` derives its `z.enum` tuple from it
 * rather than keeping a second copy. It used to keep both a `LOCATIONS` tuple and a
 * `LOCATION_LABELS` map that had to be edited together (POS2-1176).
 */
export const DEVICE_LOCATION_OPTIONS = [
  { value: "prep_area", labelKey: "settings.locationPrepArea" },
  { value: "service_line", labelKey: "settings.locationServiceLine" },
  { value: "wash_up", labelKey: "settings.locationWashUp" },
  { value: "cold_store", labelKey: "settings.locationColdStore" },
] as const satisfies readonly ConfigOptionKey[];

/** The values of an option list as the non-empty tuple `z.enum` requires.
 *
 * Derived rather than written out, so a page's schema and its menu cannot disagree — `devices.tsx`
 * kept a `LOCATIONS` tuple beside a `LOCATION_LABELS` map, and `sites.tsx` / `kitchens.tsx` each
 * spelled their five or six values into `z.enum` twice more (POS2-1176).
 *
 * The cast preserves the *literal union* rather than widening to `string`: a form field typed
 * `"hospital" | "corporate" | …` is what makes `site.site_type as EditForm["site_type"]` a real
 * check instead of a no-op.
 */
function enumValues<const T extends readonly { value: string }[]>(
  options: T,
): [T[number]["value"], ...T[number]["value"][]] {
  return options.map((o) => o.value) as [T[number]["value"], ...T[number]["value"][]];
}

export const SITE_TYPE_VALUES = enumValues(SITE_TYPE_OPTIONS);
export const KITCHEN_TYPE_VALUES = enumValues(KITCHEN_TYPE_OPTIONS);
export const DEVICE_LOCATION_VALUES = enumValues(DEVICE_LOCATION_OPTIONS);

/** The four catalogue tiers, named the way `pages/settings/catalogue.tsx` already names them.
 *
 * `sourceMaster` and `sourceCountry` are **reused rather than reinvented**: that page renders the
 * same `master_default` / `country_default` / `org_default` / `site_custom` system as a catalogue's
 * "source", its German is reviewed, and a second set of keys for the same four tiers would be two
 * names for one thing — plus a check-5 failure the moment their German differed (POS2-1176).
 */
export const CATALOGUE_TIER_OPTIONS = [
  { value: "master_default", labelKey: "settings.sourceMaster" },
  { value: "country_default", labelKey: "settings.sourceCountry" },
  { value: "org_default", labelKey: "settings.sourceOrg" },
  { value: "site_custom", labelKey: "settings.sourceCustom" },
] as const satisfies readonly ConfigOptionKey[];

/** The label for one stored value, or the value itself when the list does not know it.
 *
 * Every caller previously wrote `LABELS[x] ?? x` inline, so a row holding an enum value the app
 * predates still shows something. One of the four forgot the `??` and rendered nothing at all
 * (POS2-1176), which is the argument for this existing rather than the pattern being repeated.
 */
export function optionLabel(
  t: TFunction,
  options: readonly ConfigOptionKey[],
  value: string | null | undefined,
): string {
  if (!value) return "";
  const match = options.find((o) => o.value === value);
  return match ? t(match.labelKey) : value;
}

/** Resolve a keyed list for a `<select>`. `TIMEZONE_OPTIONS` needs no equivalent: an IANA
 * name is an identifier, not copy, and reads the same in every language this app ships. */
export function configOptions(t: TFunction, options: readonly ConfigOptionKey[]): ConfigOption[] {
  return options.map((o) => ({ value: o.value, label: t(o.labelKey) }));
}

/** The options to render for a field whose stored value is not on the list.
 *
 * A row written before these lists existed — or by an admin through the API — can hold a
 * value the menu does not offer. Dropping it would make the form unsubmittable with no
 * way out but changing the value, and for a timezone that silently moves the org's
 * reporting day. So the current value is always offered, marked as such.
 */
export function withCurrentValue(
  t: TFunction,
  options: readonly ConfigOption[],
  current: string | null | undefined,
): ConfigOption[] {
  if (!current || options.some((o) => o.value === current)) return [...options];
  return [...options, { value: current, label: t("settings.optionCurrent", { value: current }) }];
}
